import React, { useMemo } from 'react';
import classes from 'new/components/settings/VendorBuilder/VendorBuilder.module.scss';
import { Section } from 'new/components/settings/VendorBuilder/components/Section';
import { Stack } from '@mui/material';
import { AuthTemplateDynamicConfig } from 'new/components/settings/VendorBuilder/AuthConfig/AuthTemplateDynamicConfig';
import { authTypes, exchangeTypes } from 'new/components/settings/VendorBuilder/selectTypes';
import { AuthParams } from 'new/components/settings/VendorBuilder/AuthConfig/AuthParams';
import { useVendorBuilder } from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { AuthTemplateTest } from 'new/components/settings/VendorBuilder/AuthConfig/AuthTemplateTest';
import { AdvancedTemplateConfig } from 'new/components/settings/VendorBuilder/AuthConfig/AdvancedTemplateConfig/AdvancedTemplateConfig';
import { FormBuilder } from 'new/components/settings/VendorBuilder/FormControlBuilder';
import Typography from 'new/elements/Typography';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';

interface AuthTemplateProps {
  template: any;
  index: number;
}

const nameFields = (index, vendorAuthType, vendor) => [
  {
    type: 'text',
    label: (
      <>
        Template Name <code>auth_templates[{index}].name</code>
      </>
    ),

    name: `auth_templates[${index}].name`,
    prefix: { vendorPath: 'name' },
    required: true,
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          Unique Name (<code>auth_template[{index}].name</code>)
        </Typography>
        <Typography variant="body2">
          Set a unique name for this auth template. This is the <code>name</code> property of the{' '}
          <code>auth_template</code> object in the template spec.
          <br />
        </Typography>
        <Typography variant="body2">
          <b>Recommendation:</b> To ensure unique names, prefix it with the{' '}
          <code>vendor.name_</code>. Ex: <code>li_mktg_api_</code> for LinkedIn Marketing API.
          <br /> Name choice has no impact other than it being the unique index. So use anything,
          maybe something close to the type of authentication so it is easy to understand for
          tempalte managers.
          <br />
          For ex: Support for Facebook system user authentication requires Nexla{' '}
          <code>"auth_type":"NONE"</code>. But instead of setting{' '}
          <code>"name": "fb_ads_api.none"</code> we might say{' '}
          <code>"name": "fb_ads_api.sys_user"</code>
        </Typography>
      </Stack>
    ),
  },
  {
    type: 'text',
    label: (
      <>
        Display Name <code>auth_templates[{index}].display_name</code>
      </>
    ),

    name: `auth_templates[${index}].display_name`,
    required: true,
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          Display Name (<code>auth_template[{index}].display_name</code>)
        </Typography>
        <Typography variant="body2">
          Set the name user will see when choosing authentication mechnism. This is the{' '}
          <code>display_name</code> property of the <code>auth_template</code> object in the
          template spec.
          <br />
        </Typography>
        <Typography variant="body2">
          <b>Recommendation:</b> This is visible to the user and is supposed to guide them with{' '}
          <em>How do I want to authenticate</em>. Use words that are easier for user of that 3rd
          party product to understand. <br />
          For ex: Support for Facebook system user authentication requires Nexla{' '}
          <code>"auth_type":"NONE"</code>. But instead of setting{' '}
          <code>display_name: "No Authentication"</code> we might say{' '}
          <code>display_name: "System User Authentication"</code>
        </Typography>
      </Stack>
    ),
  },
  {
    type: 'text',
    label: (
      <>
        Display Name <code>auth_templates[{index}].description</code>
      </>
    ),

    name: `auth_templates[${index}].description`,
    required: false,
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          Display Name (<code>auth_template[{index}].description</code>)
        </Typography>
        <Typography variant="body2">
          Set the description user will see when choosing authentication mechnism. This is the{' '}
          <code>description</code> property of the <code>auth_template</code> object in the template
          spec.
          <br />
        </Typography>
        <Typography variant="body2">
          <b>Recommendation:</b> This is visible to the user and is supposed to guide them with{' '}
          <em>How do I want to authenticate</em>. Use words that are easier for user of that 3rd
          party product to understand. <br />
          Additionally, conside adding link to authentication page of vendor's documentation if
          present.
        </Typography>
      </Stack>
    ),
  },
  {
    type: 'select',
    defaultValue: authTypes[0].value,
    label: (
      <>
        Authentication Type <code>auth_templates[{index}].config["auth.type"]</code>
      </>
    ),

    required: true,
    items: authTypes,
    name: `auth_templates[${index}].config["auth.type"]`,
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          Authentication Type [<code>auth_template[{index}].config.auth_type</code>]
        </Typography>
        <Typography variant="body2">
          Choose the Nexla authentication type. This is to auto-populate the [
          <code>auth_template[].config.auth_type</code>] property and other related properties.
          <br />
        </Typography>
        <Typography variant="body2">
          <b>Note:</b>
          <ol>
            <li>
              This UI auto-generates some <code>config</code> properties that are required.
            </li>
            <li>
              That does not mean it generates <strong>all</strong> necessary config properties.
            </li>
            <li>
              Please visually inspect the <code>auth_template[].config</code> object and
              add/override any missing entries in the <strong>Auth Template Config</strong> section.
            </li>
          </ol>
          Real souce of truth is in the
          <a href="https://developers.nexla.com/reference#tag/Credentials/operation/create_data_credential">
            API docs
          </a>
          <code>
            Please make sure you do a visual inspection that config has all the entries you need.
          </code>
        </Typography>
      </Stack>
    ),
  },
  {
    type: 'select',
    dependsOn: {
      [`auth_templates[${index}].config['auth.type']`]: ['OAUTH1', 'OAUTH2'],
    },
    required: true,
    label: (
      <>
        Type of {vendor.auth_templates[index]?.config?.['auth.type']}
        exchange
        <code>
          auth_templates[{index}].config["
          {vendorAuthType.toLowerCase()}.token_exchange_type"]
        </code>
      </>
    ),

    name: `auth_templates[${index}].config["${vendorAuthType.toLowerCase()}.token_exchange_type"]`,
    items: exchangeTypes,
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          Type of {vendor.auth_templates[index]?.config?.['auth.type']}
          exchange{' '}
          <code>
            auth_templates[{index}].config["
            {vendorAuthType.toLowerCase()}.token_exchange_type"]
          </code>
        </Typography>
        <Typography variant="body2">
          OAuth authentication can support 2-legged or 3-legged interaction.
          <br />
        </Typography>
        <Typography variant="body2">
          <b>Recommendations:</b>
          <ol>
            <li>If vendor supports it, choose 2-legged over 3-legged.</li>
            <li>
              Pay attention to refresh mechanism. If vendor supports it, you must include
              information about how to refresh token.
            </li>
          </ol>
          <br />
          <b>Note:</b>
          <ol>
            <li>
              <b>For 3-legged OAuth1:</b> You do not need to attach{' '}
              <code>oauth_caloauth1a.request_url_param.oauth_callbacklback_url</code>. UI will add
              it automatically to Request URL.
            </li>
            <li>
              <b>For 3-legged OAuth2:</b> You do not need to attach{' '}
              <code>oauth2.auth_url_param.redirect_uri</code> or{' '}
              <code>oauth2.token_url_param.code</code>. UI adds it automatically to Authorization
              URL and Token URL respectively.
            </li>
          </ol>

          <br />
        </Typography>
      </Stack>
    ),
  },
];

export const AuthTemplate = (props: AuthTemplateProps) => {
  const { index, template } = props;
  const { vendor } = useVendorBuilder();

  const vendorAuthType = vendor.auth_templates[index]?.config?.['auth.type'] || '';

  const fields = useMemo(
    () => nameFields(index, vendorAuthType, vendor),
    [
      index,
      vendorAuthType,
      vendor.auth_templates[index].config[`${vendorAuthType.toLowerCase()}.token_exchange_type`],

      vendor.auth_templates[index].config[`auth.type`],
    ],
  );

  return (
    <Stack className={classes.round_container}>
      <Section
        defaultExpanded
        title={
          <KnowledgePanel
            alwaysShowUnderline
            knowledgeContent={
              <div style={{ padding: 'var(--spacing-lg)' }}>
                <Typography variant="body2">
                  Set some basic information about the template that allows user to understand what
                  type of authentication.
                  <br />
                  Also choose type of authentication. This adds some default entries in{' '}
                  <code>auth_templates[].config</code> section. UI doesn't care about this object.
                  You can customize this object in the <strong>Auth Template Config</strong>{' '}
                  section.
                </Typography>
              </div>
            }
          >
            Template #{index + 1}: Basics
          </KnowledgePanel>
        }
        gap="16px"
      >
        <FormBuilder fields={fields} />
      </Section>
      <AuthParams templateIndex={index} />
      <AuthTemplateDynamicConfig
        type={vendor.auth_templates[index]?.config?.['auth.type']}
        index={index}
      />

      <AuthTemplateTest templateIndex={index} />
      <AdvancedTemplateConfig templateIndex={index} />
    </Stack>
  );
};
